import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Card, Empty } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

interface RecordNotFoundProps {
  /** Translation key, e.g. "recordNotFound.project.title". */
  titleKey: string
  /** Where the way back leads, e.g. the Projects list. */
  backTo: string
  /** Translation key, e.g. "recordNotFound.project.back". */
  backLabelKey: string
}

/**
 * A record the link points at no longer exists, or the user may not open it.
 * Says so in words and always offers a way back, so the page is never a dead
 * end.
 */
export function RecordNotFound({
  titleKey,
  backTo,
  backLabelKey
}: RecordNotFoundProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <Card size={"small"} data-testid={"record-not-found"}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <>
            <div className={"font-semibold"}>{t(titleKey)}</div>
            <div>{t("recordNotFound.hint")}</div>
          </>
        }
      >
        <Button
          type={"primary"}
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backTo)}
        >
          {t(backLabelKey)}
        </Button>
      </Empty>
    </Card>
  )
}

/**
 * The record could not be read for a reason other than a 404 (a server error,
 * a dropped connection). The record may well exist, so offer a retry rather
 * than saying it is gone.
 */
export function RecordLoadFailed({
  onRetry,
  backTo,
  backLabelKey
}: {
  onRetry: () => void
  backTo: string
  backLabelKey: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <Card size={"small"} data-testid={"record-load-failed"}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("recordNotFound.failed")}
      >
        <div className={"flex justify-center gap-2"}>
          <Button type={"primary"} icon={<ReloadOutlined />} onClick={onRetry}>
            {t("recordNotFound.retry")}
          </Button>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backTo)}>
            {t(backLabelKey)}
          </Button>
        </div>
      </Empty>
    </Card>
  )
}
